import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, FileText, DollarSign, TrendingUp, AlertCircle, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contractService } from '../../services/contract.service';
import { ContractStatus, ContractType } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';

export const ContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>(['ACTIVE', 'IN_PROGRESS']);
  const [typeFilter, setTypeFilter] = useState<string>('');

  const { data: contracts, isLoading } = useQuery({
    queryKey: ['contracts'],
    queryFn: contractService.getAll,
  });

  const { data: summary } = useQuery({
    queryKey: ['contracts', 'summary'],
    queryFn: contractService.getSummary,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: ContractStatus): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case ContractStatus.DRAFT:
        return 'default';
      case ContractStatus.ACTIVE:
        return 'info';
      case ContractStatus.IN_PROGRESS:
        return 'warning';
      case ContractStatus.COMPLETED:
        return 'success';
      case ContractStatus.CANCELLED:
        return 'error';
      case ContractStatus.ON_HOLD:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: ContractType): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (type) {
      case ContractType.TENDER:
        return 'info';
      case ContractType.REGULAR:
        return 'success';
      case ContractType.ONE_TIME:
        return 'default';
      case ContractType.RECURRING:
        return 'warning';
      default:
        return 'default';
    }
  };

  const filteredContracts = contracts?.filter((contract) => {
    const matchesSearch =
      contract.contractName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.contractNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(contract.status);
    const matchesType = !typeFilter || contract.contractType === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const activeContracts = summary?.filter(
    (c) => c.status === ContractStatus.ACTIVE || c.status === ContractStatus.IN_PROGRESS
  ).length || 0;

  const totalValue = summary?.reduce((sum, c) => sum + c.contractValue, 0) || 0;
  const avgCompletion =
    summary && summary.length > 0
      ? summary.reduce((sum, c) => sum + c.completionPercentage, 0) / summary.length
      : 0;
  const outstandingAmount = summary?.reduce((sum, c) => sum + c.outstandingAmount, 0) || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-charcoal mb-2">Contract Management</h1>
          <p className="text-gray-600">Manage tenders and multi-trip contracts</p>
        </div>
        <Button onClick={() => navigate('/contracts/new')} variant="primary">
          <Plus size={20} className="mr-2" />
          New Contract
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Active Contracts</p>
              <FileText className="text-blue-600" size={20} />
            </div>
            <p className="text-2xl font-bold text-blue-600">{activeContracts}</p>
            <p className="text-xs text-gray-500 mt-1">Currently active</p>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Total Contract Value</p>
              <DollarSign className="text-green-600" size={20} />
            </div>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(totalValue)}</p>
            <p className="text-xs text-gray-500 mt-1">Total value</p>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Average Completion</p>
              <TrendingUp className="text-purple-600" size={20} />
            </div>
            <p className="text-2xl font-bold text-purple-600">{avgCompletion.toFixed(1)}%</p>
            <p className="text-xs text-gray-500 mt-1">Across all contracts</p>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Outstanding Invoices</p>
              <AlertCircle className="text-orange-600" size={20} />
            </div>
            <p className="text-2xl font-bold text-orange-600">{formatCurrency(outstandingAmount)}</p>
            <p className="text-xs text-gray-500 mt-1">Pending payment</p>
          </div>
        </Card>
      </div>

      <Card>
        <div className="mb-4 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by contract name, client, or number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Types</option>
              {Object.values(ContractType).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-2">
            {Object.values(ContractStatus).map((status) => (
              <label key={status} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={statusFilter.includes(status)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setStatusFilter([...statusFilter, status]);
                    } else {
                      setStatusFilter(statusFilter.filter((s) => s !== status));
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">{status}</span>
              </label>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading contracts...</p>
          </div>
        ) : filteredContracts && filteredContracts.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contract Number</TableHead>
                <TableHead>Contract Name</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Weight Progress</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Completion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContracts.map((contract) => (
                <TableRow key={contract.id}>
                  <TableCell className="font-mono text-sm">
                    <div 
                      className="cursor-pointer hover:text-blue-600"
                      onClick={() => navigate(`/contracts/${contract.id}`)}
                    >
                      {contract.contractNumber}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">
                    <div 
                      className="cursor-pointer hover:text-blue-600"
                      onClick={() => navigate(`/contracts/${contract.id}`)}
                    >
                      {contract.contractName}
                    </div>
                  </TableCell>
                  <TableCell>{contract.clientName}</TableCell>
                  <TableCell>
                    <Badge variant={getTypeColor(contract.contractType)}>{contract.contractType}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm">
                        {contract.completedWeightTons} / {contract.totalWeightTons} tons
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full"
                          style={{
                            width: `${Math.min(
                              (contract.completedWeightTons / contract.totalWeightTons) * 100,
                              100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(contract.contractValue)}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusColor(contract.status)}>{contract.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-12 h-12 rounded-full border-4 border-gray-200 flex items-center justify-center relative">
                        <svg className="absolute inset-0 w-12 h-12 transform -rotate-90">
                          <circle
                            cx="24"
                            cy="24"
                            r="20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="4"
                            className="text-green-600"
                            strokeDasharray={`${
                              ((contract.completedWeightTons / contract.totalWeightTons) * 100 * 125.6) / 100
                            } 125.6`}
                          />
                        </svg>
                        <span className="text-xs font-semibold">
                          {((contract.completedWeightTons / contract.totalWeightTons) * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No contracts found</p>
            <Button onClick={() => navigate('/contracts/new')} variant="primary">
              <Plus size={20} className="mr-2" />
              Create Your First Contract
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};
